/**
 * Statsig feature gate names. Primary consumer of the analytics layer's gate
 * checks — keep every gate name here, never inline.
 */
export const CTRL_K_SEARCH_GATE = 'ctrl_k_search';

/** Billing: the Settings → Billing section (Lemon Squeezy checkout). Tiers and
  * pricing are undecided (SP-38), so this stays dark outside dev. */
export const BILLING_GATE = 'billing';

/**
 * Billing → Manage subscription (the Lemon Squeezy customer portal).
 *
 * Separate from BILLING_GATE on purpose: that one controls whether the
 * Billing section exists at all, so reusing it here would gate nothing —
 * the button only renders when the section already does.
 *
 * Closed until the Lemon Squeezy store is activated. The portal endpoint
 * works and returns a valid signed url, but Lemon Squeezy refuses to render
 * the portal for an unactivated store ("This store has not been
 * activated"), and it answers our API call with a 200 — so the backend
 * cannot detect the condition and the user would land on an error page.
 */
export const BILLING_PORTAL_GATE = 'billing_portal';

/**
 * Dark mode: the Settings → Appearance theme picker AND whether the stored
 * theme is applied at all (src/preferences/theme-preference.ts keeps the app
 * light while this is off or unresolved-without-cache). Remove once rolled out.
 */
export const DARK_MODE_GATE = 'dark_mode';

/**
 * Quick Access: the crochet editor's W menu of favorited actions, and the
 * right-click "Add to / Remove from Quick Access" popup on selection-menu and
 * ⌘K rows. Closed → no W listener, no popup, rows keep the native context
 * menu. Kept as a gate so it can be rolled out gradually / A/B tested.
 */
export const QUICK_ACCESS_GATE = 'quick_access';

/**
 * The paid offline app: the Settings → Offline app section and the signed-in
 * /offline route that mints an install token (SP-84). The offline build
 * itself never shows these (OFFLINE_GATES keeps it false there).
 */
export const OFFLINE_APP_GATE = 'offline_app';

/**
 * Every gate above, as a list. Keep in sync with the constants in this file
 * (test/offline-gates.test.ts fails if one is missing). It exists so
 * `OFFLINE_GATES` (src/offline/offline-gates.ts) can be a total
 * `Record<FeatureGateName, boolean>`: adding a gate without deciding its
 * offline value is then a typecheck error.
 */
export const ALL_FEATURE_GATES = [
    CTRL_K_SEARCH_GATE,
    BILLING_GATE,
    BILLING_PORTAL_GATE,
    DARK_MODE_GATE,
    QUICK_ACCESS_GATE,
    OFFLINE_APP_GATE,
] as const;

export type FeatureGateName = (typeof ALL_FEATURE_GATES)[number];
